import React, { useState, useEffect } from "react";
import "./ProductModal.css";
import { AiOutlineClose } from "react-icons/ai";
import { addProduct, updateProduct } from "../../services/productService";
import { getCategories } from "../../services/categoryService";

function ProductModal({ isOpen, onClose, product, categories, onProductUpdated, setPopupMessage }) {
    const [formData, setFormData] = useState({
        name: "",
        categoryId: "",
        brand: "",
        country: "",
        price: "",
        description: "",
        isImported: false,
        status: "1" // Default to active
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const isEditMode = !!product;

    // Load product data when in edit mode
    useEffect(() => {
        if (product) {
            console.log("Loading product for editing:", product);
            // Format the data for the form
            setFormData({
                name: product.name || "",
                categoryId: product.category_id || "",
                brand: product.brand || "",
                country: product.country || "",
                price: product.price || "",
                description: product.description || "",
                isImported: product.is_imported === 1 || product.is_imported === true,
                status: Number(product.status) === 1 ? "1" : "0"
            });
        } else {
            // Reset form for adding a new product
            setFormData({
                name: "",
                categoryId: "",
                brand: "",
                country: "",
                price: "",
                description: "",
                isImported: false,
                status: "1" // Default to active
            });
        }
    }, [product]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (type === 'checkbox') {
            setFormData({ ...formData, [name]: checked });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const validateForm = () => {
        if (!formData.name.trim()) {
            setPopupMessage("Product name is required", "error");
            return false;
        }
        if (!formData.categoryId) {
            setPopupMessage("Please select a category", "error");
            return false;
        }
        if (!formData.price || formData.price <= 0) {
            setPopupMessage("Please enter a valid price", "error");
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            // Format price to ensure it's a number
            const apiData = {
                ...formData,
                price: parseFloat(formData.price)
            };

            if (isEditMode) {
                await updateProduct(product.id, apiData);
                setPopupMessage("Product updated successfully!", "success");
            } else {
                await addProduct(apiData);
                setPopupMessage("Product added successfully!", "success");
            }

            onProductUpdated();
            onClose();
        } catch (error) {
            console.error(`Error ${isEditMode ? 'updating' : 'adding'} product:`, error);
            setPopupMessage(`Error: ${error.message || `Failed to ${isEditMode ? 'update' : 'add'} product`}`, "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="product-modal">
            <div className="product-modal-content">
                <button className="product-close-btn" onClick={onClose}>
                    <AiOutlineClose />
                </button>
                <h2>{isEditMode ? "Edit Product" : "Add Product"}</h2>
                <div className="product-modal-divider"></div>

                <form>
                    <div className="product-form-group">
                        <label>Product Name: <span className="required">*</span></label>
                        <input type="text" name="name" value={formData.name} onChange={handleChange} />
                    </div>

                    {/* Category and Brand side by side */}
                    <div className="form-row">
                        <div className="product-form-group">
                            <label>Category: <span className="required">*</span></label>
                            <select name="categoryId" value={formData.categoryId} onChange={handleChange}>
                                <option value="">Select Category</option>
                                {categories.map((category) => (
                                    <option key={category.id} value={category.id}>
                                        {category.category_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="product-form-group">
                            <label>Brand:</label>
                            <input type="text" name="brand" value={formData.brand} onChange={handleChange} />
                        </div>
                    </div>

                    <div className="product-form-group">
                        <label>Country:</label>
                        <input type="text" name="country" value={formData.country} onChange={handleChange} />
                    </div>

                    <div className="product-form-group">
                        <label>Price: <span className="required">*</span></label>
                        <input type="number" name="price" value={formData.price} onChange={handleChange} />
                    </div>

                    <div className="product-form-group">
                        <label>Description:</label>
                        <textarea name="description" value={formData.description} onChange={handleChange}></textarea>
                    </div>

                    {/* Status and Imported checkbox side by side */}
                    <div className="form-row">
                        <div className="product-form-group status-group">
                            <label>Status:</label>
                            <select name="status" value={formData.status} onChange={handleChange}>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </select>
                        </div>

                        <div className="product-form-group checkbox-group">
                            <label>
                                <input
                                    type="checkbox"
                                    name="isImported"
                                    checked={formData.isImported}
                                    onChange={handleChange}
                                />
                                Imported Product
                            </label>
                        </div>
                    </div>

                    <button
                        className="product-register-btn"
                        type="button"
                        onClick={handleSave}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? "Saving..." : (isEditMode ? "Update Product" : "Add Product")}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default ProductModal;